import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { RbacService } from '../../../common-api/system-management-api';
import { SeverityLevelEnum } from '../../../common/public-api';
import { genCommonObserverObj, isNullOrEmpty } from '../../../core/utils';

export const TOAST_WIDGET_KEY = 'app_toast_widget';
const PROCESS_END_MSG = 'Process End';

/**
 * Toast service
 *
 * @export
 * @class ToastService
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  constructor(
    private _messageService: MessageService,
    private rbacService: RbacService,
    private translateService: TranslateService
  ) {}

  info(msg: string, sticky?: boolean): void {
    this._addSingle(
      SeverityLevelEnum.Info,
      'Information',
      msg,
      sticky,
      TOAST_WIDGET_KEY
    );
  }

  success(msg: string, sticky?: boolean): void {
    this._addSingle(
      SeverityLevelEnum.Success,
      'toastSuccess',
      msg,
      sticky,
      TOAST_WIDGET_KEY
    );
  }

  warn(msg: string, sticky?: boolean): void {
    this._addSingle(
      SeverityLevelEnum.Warning,
      'Warning',
      msg,
      sticky,
      TOAST_WIDGET_KEY
    );
  }

  error(msg: string, sticky?: boolean): void {
    this._addSingle(
      SeverityLevelEnum.Error,
      'Error',
      msg,
      sticky,
      TOAST_WIDGET_KEY
    );
  }

  private _addSingle(
    severity: string,
    summary: string,
    msg: string,
    sticky?: boolean,
    key?: string,
    data?: any
  ): void {
    this._messageService.add({
      severity,
      summary,
      detail: msg,
      sticky,
      key,
      data,
      life: 5000,
    });
  }
  /**
   * 新增多筆提示訊息
   * @param messages
   */
  addMultiple(messages: any): void {
    this._messageService.addAll(messages);
  }

  /**
   * 清除所有訊息
   */
  clear(): void {
    this._messageService.clear();
  }
  apiError(detail: string): void {
    this._addSingle('error', 'Unknown Error', detail, true, TOAST_WIDGET_KEY);
  }
  /**
   * 流程資訊
   *
   * @param {string} [userNo] 員編
   * @param {string} [userName] 姓名
   * @memberof ToastService
   */
  processing(userNo?: string, userName?: string): void {
    if (isNullOrEmpty(userNo) === false && isNullOrEmpty(userName)) {
      this.rbacService.rbacUserProfileUserNoGet(userNo!).subscribe(
        genCommonObserverObj({
          next: (result) => {
            userName = result.userName;
            const data = { toastType: 'process', userNo, userName };
            this._addSingle(
              'success',
              'toastSuccess',
              '',
              undefined,
              TOAST_WIDGET_KEY,
              data
            );
          },
        })
      );
    } else {
      const data = { toastType: 'process', userNo, userName };
      this._addSingle(
        'success',
        'toastSuccess',
        '',
        undefined,
        TOAST_WIDGET_KEY,
        data
      );
    }
  }
  /**
   * 顯示下一關資訊+下一關處理人員
   */
  async nextProcessString(stringReturnedFromDB: string): Promise<void> {
    if (!isNullOrEmpty(stringReturnedFromDB)) {
      const splitString = stringReturnedFromDB.split('-');
      const nextStage = splitString[0];
      let nextProcessor = splitString.length > 1 ? splitString[1] : ' ';

      const checkIsMultipleProcessors = nextProcessor.split(',');
      if (checkIsMultipleProcessors.length > 1) {
        nextProcessor = '';
        checkIsMultipleProcessors.forEach(async (processor) => {
          if (!isNullOrEmpty(processor)) {
            const username = await this.getUserName(processor);
            nextProcessor = nextProcessor + '\n' + processor + ' ' + username;
          }
        });
      } else {
        if (!isNullOrEmpty(nextProcessor)) {
          const username = await this.getUserName(nextProcessor);
          if (!isNullOrEmpty(username)) {
            nextProcessor = nextProcessor + ' ' + username;
          } else {
            nextProcessor = '';
          }
        }
      }
      const finalStr = this.translateService
        .instant('Library.UI.ToastService.nextStageProcessor')
        .replace('{nextStage}', nextStage)
        .replace('{nextProcessor}', nextProcessor);
      this.success(finalStr);
    }
  }
  getUserName(userNo: string): Promise<string> {
    return new Promise((resolve) => {
      this.rbacService.rbacUserProfileUserNoGet(userNo).subscribe((user) => {
        resolve(user?.userName!);
      });
    });
  }
  processingWithMultipleProcessor(
    userInfos: { userNo?: string; userName?: string }[]
  ): void {
    const userNos = userInfos
      .map((x) => x.userNo)
      .filter((x) => isNullOrEmpty(x) === false) as string[];
    if (userNos.length > 0) {
      this.rbacService.rbacUserProfileUserNoSearchPost(userNos).subscribe(
        genCommonObserverObj({
          next: (results) => {
            userInfos.forEach((x) => {
              x.userName = results.find((y) => y.userNo === x.userNo)?.userName;
            });
            const data = { toastType: 'processMultiple', userInfos: userInfos };
            this._addSingle(
              'success',
              'Success',
              '',
              undefined,
              TOAST_WIDGET_KEY,
              data
            );
          },
        })
      );
    }
  }

  /**
   * 流程結束
   *
   * @memberof ToastService
   */
  processEnd(): void {
    this._addSingle(
      'success',
      'toastSuccess',
      PROCESS_END_MSG,
      undefined,
      TOAST_WIDGET_KEY
    );
  }
}
